import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_VideoBackgroundRemover = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/video-background-remover',
			name: 'Video Background Remover — Delete Video Background Online | Lift',
			description:
				'Clear the background from any video with Lift — upload a clip, choose your resolution, and AI erases everything behind your subject in seconds. Try Lift now.',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/video-background-remover#product' },
				{ '@id': 'https://liftapp.ai/video-background-remover#howto' },
				{ '@id': 'https://liftapp.ai/video-background-remover#benefits' },
				{ '@id': 'https://liftapp.ai/video-background-remover#faq' },
				{ '@id': 'https://liftapp.ai/video-background-remover#why-lift' },
				{ '@id': 'https://liftapp.ai/video-background-remover#explore-tools' },
				{ '@id': 'https://liftapp.ai/video-background-remover#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/video-background-remover#product',
			name: 'Video Background Remover Online',
			description:
				"Automatically remove the background from any video with AI. Upload a clip, choose a resolution, and download the result with the backdrop erased in seconds. Try Lift's video background remover now!",
			url: 'https://liftapp.ai/video-background-remover',
			fileFormat: [
				'video/mp4',
				'video/quicktime',
				'video/x-msvideo',
				'video/webm',
				'video/x-matroska',
				'video/mpeg',
			],
			memoryRequirements: 'Max file size: 500MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/video-background-remover#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload Your Video',
					text: 'Drop in a clip from your device — MP4, MOV, MKV, WEBM, GIF, and more are supported.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Select Video Resolution',
					text: 'Pick the output quality you need, from SD up to Ultra HD.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Remove The Background',
					text: "Tap Remove background and Lift's AI isolates your subject in every frame, clearing away the scenery automatically.",
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download Your Result',
					text: 'Preview the finished clip and download it, or switch resolution to generate a new version.',
				},
			],
		},
		// benefits
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/video-background-remover#benefits',
			name: 'Who can benefit from the Lift Video Background Remover?',
			description:
				'Lift Video Background Remover helps content creators, marketers, video editors, teachers, and everyday video takers cut the background out of any clip.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: "Content creators — Shorts and reels stand out when the subject isn't tied to the room they were filmed in. Remove the background from your video and drop yourself into any edit, intro, or overlay.",
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: "Marketers and online sellers — Product clips look more professional on a clean, distraction-free backdrop. Lift isolates the item in every frame so your promo stays focused on what you're selling.",
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Video editors — Manual rotoscoping eats hours for every single clip. Lift separates your subject frame by frame automatically, so clean cutouts for composites and overlays are ready in minutes.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Teachers and presenters — Talking-head clips blend into slides and course videos more naturally without a wall behind you. Erase the scene and layer yourself over any presentation.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Everyday video takers — Not every clip is filmed in a photogenic spot. Cut out the surroundings and keep the moment — for a meme, a sticker, or a family montage.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.4f82ac71.webp',
				name: 'Video background remover tool',
				description: 'Video background remover — Lift',
			},
		},
		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/video-background-remover#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift Video Background Remover?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Video Background Remover is Lift's AI video tool that automatically removes the background from a clip. It detects people and objects in every frame and separates them from the scene behind them — keeping your subject sharp while the surroundings disappear.",
					},
				},
				{
					'@type': 'Question',
					name: 'How do I remove the background from a video?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload your clip to Lift, choose the video resolution you want, and tap Remove background. The AI processes each frame automatically and shows a preview you can download right away.',
					},
				},
				{
					'@type': 'Question',
					name: 'Do I need a green screen to remove a video background?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "No. Lift's AI recognizes your subject in regular footage — no chroma key setup or manual masking required. It also works on clips filmed against a colored screen, erasing that backdrop just as cleanly. Steady footage with minimal camera movement produces the best cutout.",
					},
				},
				{
					'@type': 'Question',
					name: 'Can I choose the quality of the output video?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Before removing the background you select a video resolution, from SD up to Ultra HD. After processing, you can switch to a different resolution and generate a new result from the same original upload.',
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to upload my videos to Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes your videos securely and uses them only to generate your results.',
					},
				},
			],
		},
		// why-lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/video-background-remover#why-lift',
			name: 'Why Lift Video Background Remover',
			description:
				'Discover the advantages of Lift Video Background Remover: high-quality results, intelligent rendering, cross-platform support, and budget-friendly pricing.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description:
						'Available on iOS, Android, and Web for seamless access.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyLift.7c19de84.webp',
				name: 'Why use Lift Video Background Remover',
				description: 'Why use Lift Video Background Remover — Lift.',
			},
		},
		// explore-tools
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/video-background-remover#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		// all-in-one
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/video-background-remover#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Video Background Remover',
					item: 'https://liftapp.ai/video-background-remover',
				},
			],
		},
	],
};
